import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvitationDto } from 'woofwoof-api';
import InvitationClinicCard from './invitation/InvitationClinicCard';
import InvitationClinicCardSkeleton from './invitation/InvitationClinicCardSkeleton';
import InvitationStatusCard from './invitation/InvitationStatusCard';
import InvitationSwitcher from './invitation/InvitationSwitcher';
import { useInvitationDecisions } from './invitation/useInvitationDecisions';

interface Props {
  invites: InvitationDto[];
  onAccept: (invitation: InvitationDto) => Promise<void>;
  onReject: (invitation: InvitationDto) => Promise<void>;
  onDashboard: () => Promise<void> | void;
  onRetry: () => void;
  isLoading?: boolean;
  isError?: boolean;
}

/** Pending clinic invitations, one card at a time, shared by onboarding and the dashboard. */
export default function InvitationAcceptance({
  invites,
  onAccept,
  onReject,
  onDashboard,
  onRetry,
  isLoading,
  isError,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const decisions = useInvitationDecisions({
    invites,
    onAccept,
    onReject,
    onDashboard,
  });
  const { invitation } = decisions;

  return (
    <Stack
      component="section"
      aria-label={String(t('invitationAcceptance.pageTitle'))}
      gap={2}
      sx={(theme) => ({ width: 1, maxWidth: theme.spacing(110), mx: 'auto' })}
    >
      {isLoading ? (
        <InvitationClinicCardSkeleton />
      ) : isError || !invitation ? (
        <InvitationStatusCard
          state={isError ? 'error' : 'empty'}
          action={decisions.action}
          actionError={decisions.error}
          onRetry={onRetry}
          onDashboard={() => void decisions.decide('dashboard')}
        />
      ) : (
        <>
          <InvitationSwitcher decisions={decisions} />
          <InvitationClinicCard
            key={invitation.id}
            invitation={invitation}
            decisions={decisions}
          />
        </>
      )}
    </Stack>
  );
}
